import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";

interface GlobalSettingsContextType {
  language: string;
  setLanguage: (lang: string) => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  fontSize: number;
  setFontSize: (size: number) => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  pendingLogoutAction: (() => void) | null;
  setPendingLogoutAction: (action: (() => void) | null) => void;
}

const GlobalSettingsContext = createContext<
  GlobalSettingsContextType | undefined
>(undefined);

export const GlobalSettingsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState<string>(() => {
    // Initialize from localStorage or default to 'en'
    return localStorage.getItem("language") || "en";
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = sessionStorage.getItem("theme");
    return savedTheme === "dark";
  });
  const [fontSize, setFontSizeState] = useState<number>(() => {
    const savedSize = localStorage.getItem("fontSize");
    return savedSize ? parseFloat(savedSize) : 1;
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [pendingLogoutAction, setPendingLogoutAction] = useState<
    (() => void) | null
  >(null);

  // Effect to update i18n language when language state changes
  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  // Effect to update font size when fontSize state changes
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--dynamic-font-size",
      fontSize.toString()
    );
    localStorage.setItem("fontSize", fontSize.toString());
  }, [fontSize]);

  // Effect to update theme when isDarkMode changes
  useEffect(() => {
    sessionStorage.setItem("theme", isDarkMode ? "dark" : "light");
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
  }, [isDarkMode]);

  const setLanguage = (lang: string) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
  };

  const setFontSize = (size: number) => {
    setFontSizeState(size);
    localStorage.setItem("fontSize", size.toString());
  };

  const increaseFontSize = () => {
    setFontSizeState((prev) => Math.min(prev + 0.1, 1.2));
  };

  const decreaseFontSize = () => {
    setFontSizeState((prev) => Math.max(prev - 0.1, 0.8));
  };

  const resetFontSize = () => {
    setFontSizeState(1);
    localStorage.setItem("fontSize", "1");
  };

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const value = {
    language,
    setLanguage,
    isDarkMode,
    toggleDarkMode,
    fontSize,
    setFontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    pendingLogoutAction,
    setPendingLogoutAction,
  };

  return (
    <GlobalSettingsContext.Provider value={value}>
      {children}
    </GlobalSettingsContext.Provider>
  );
};

export const useGlobalSettings = () => {
  const context = useContext(GlobalSettingsContext);
  if (context === undefined) {
    console.error(
      "useGlobalSettings must be used within a GlobalSettingsProvider"
    );
    return {
      language: "en",
      setLanguage: () => {},
      isDarkMode: false,
      toggleDarkMode: () => {},
      fontSize: 1,
      setFontSize: () => {},
      increaseFontSize: () => {},
      decreaseFontSize: () => {},
      resetFontSize: () => {},
      hasUnsavedChanges: false,
      setHasUnsavedChanges: () => {},
      pendingLogoutAction: null,
      setPendingLogoutAction: () => {},
    };
  }
  return context;
};
